// src/App.js
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, CssBaseline, useMediaQuery } from '@mui/material';

import { ColorModeContext } from './contexts/ThemeContext';
import { KioskThemeProvider, useKioskTheme } from './contexts/KioskThemeContext';
import { CartProvider, useCart } from './contexts/CartContext';
import StoreLanding from './components/StoreLanding';
import Dashboard from './components/Dashboard';
import CartPage from './components/CartPage';
import AnonymousActivityTracker from './components/AnonymousActivityTracker';
import IdleResetManager from './components/IdleResetManager';
import ErrorBoundary from './components/ErrorBoundary';
import { installGlobalErrorHandlers, reportErrorToDiscord } from './utils/error-reporter';
import { buildKioskTheme } from './theme/kioskDesignTokens';

// Wrapper component to provide current page and store info to AnonymousActivityTracker
function ActivityTrackerWrapper() {
    const location = useLocation();
    const params = useParams();
    const { storeInfo } = useCart();

    const pageTitles = {
        '/select-store': 'Select Store',
        '/': 'Home',
    };

    // Determine current page title
    let currentPage = pageTitles[location.pathname];
    if (!currentPage) {
        const pathParts = location.pathname.replace(/^\//, '').split('/');
        if (pathParts[1] === 'cart') {
            currentPage = 'Cart';
        } else if (pathParts[0]) {
            currentPage = 'Dashboard';
        } else {
            currentPage = 'Kiosk';
        }
    }

    // Get store owner info from URL params or cart context
    const storeName = params?.storeName || storeInfo?.storeName || '';
    const storeOwnerId = storeInfo?.userId || null;
    const storeOwnerUsername = storeInfo?.username || '';

    return (
        <AnonymousActivityTracker
            currentPage={currentPage}
            storeOwnerId={storeOwnerId}
            storeOwnerUsername={storeOwnerUsername}
            storeName={storeName}
        />
    );
}

// Where a shopper's own light/dark choice is remembered. Mirrors the hosted
// storefront's `ss-theme` key. TKT-2026-104509.
const SHOPPER_MODE_KEY = 'kiosk-colorMode';

function readShopperMode() {
    try {
        const v = localStorage.getItem(SHOPPER_MODE_KEY);
        return v === 'light' || v === 'dark' ? v : null;
    } catch {
        return null;
    }
}

function App() {
    // The device's own preference. This is only the LAST resort now: the shopper's
    // own choice and the store owner's configured default both outrank it. See
    // resolveMode in theme/kioskDesignTokens.js. TKT-2026-104509.
    const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
    const deviceMode = prefersDark ? 'dark' : 'light';

    // null = this shopper has not expressed a preference, so the store's default
    // applies. Set only by the header toggle, and persisted so it survives a
    // refresh. Previously this state was overwritten by the device preference on
    // every change, and was then ignored entirely for any store with a configured
    // background or card colour, which left the header toggle doing nothing.
    const [shopperMode, setShopperMode] = useState(readShopperMode);

    useEffect(() => {
        installGlobalErrorHandlers();

        // Intercept console.error so caught errors also reach Discord.
        if (typeof window !== 'undefined' && !window.__consoleErrorPatched) {
            window.__consoleErrorPatched = true;
            const _origConsoleError = console.error;
            console.error = function (...args) {
                _origConsoleError.apply(console, args);
                try {
                    const err = args.find(a => a instanceof Error) || null;
                    const axiosInfo = err?.config ? {
                        method: (err.config.method || '').toUpperCase(),
                        url: err.config.url || err.config.baseURL || '',
                        status: err.response?.status,
                        statusText: err.response?.statusText,
                        responseData: (() => { try { return JSON.stringify(err.response?.data)?.slice(0, 500); } catch { return undefined; } })(),
                    } : null;
                    const textParts = args.filter(a => typeof a === 'string').join(' ');
                    const baseMessage = err ? err.message : textParts || 'Unknown error';
                    const richMessage = axiosInfo
                        ? `${baseMessage} | ${axiosInfo.method} ${axiosInfo.url} → ${axiosInfo.status} ${axiosInfo.statusText || ''}${axiosInfo.responseData ? ' | ' + axiosInfo.responseData : ''}`
                        : (textParts && err ? `${textParts}: ${baseMessage}` : baseMessage);
                    const richError = new Error(richMessage);
                    if (err?.stack) richError.stack = err.stack;
                    reportErrorToDiscord(richError, {
                        context: 'console.error (caught)',
                        extra: axiosInfo || undefined,
                    });
                } catch (_) { /* never break the app */ }
            };
        }
    }, []);

    const { themeOverrides } = useKioskTheme();

    // What the kiosk is showing right now, given shopper choice > owner default >
    // palette luminance > device.
    // TKT-2026-107229: built together, through a builder that falls back to the
    // stock theme, so a store colour MUI cannot parse never blanks the kiosk.
    const { mode, theme } = useMemo(
        () => buildKioskTheme(deviceMode, themeOverrides, shopperMode),
        [deviceMode, themeOverrides, shopperMode],
    );

    const colorMode = useMemo(
        () => ({
            // Toggle relative to what is actually on screen, not to the previous
            // shopper choice, so the first tap always visibly flips the kiosk even
            // when the shopper has not chosen anything yet.
            toggleColorMode: () => {
                const next = mode === 'light' ? 'dark' : 'light';
                setShopperMode(next);
                try {
                    localStorage.setItem(SHOPPER_MODE_KEY, next);
                } catch {
                    /* private mode / storage disabled: choice just won't persist */
                }
            },
        }),
        [mode],
    );

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <ErrorBoundary>
                <BrowserRouter>
                    <CartProvider>
                        <ActivityTrackerWrapper />
                        <IdleResetManager />
                        <Routes>
                            <Route path='/' element={<Navigate to='/select-store' replace />} />
                            <Route path='/select-store' element={<StoreLanding />} />
                            <Route path='/:storeName' element={<Dashboard />} />
                            <Route path='/:storeName/cart' element={<CartPage />} />
                        </Routes>
                    </CartProvider>
                </BrowserRouter>
                </ErrorBoundary>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

// Outer wrapper that provides KioskThemeProvider above App
export default function AppWithProviders() {
    return (
        <KioskThemeProvider>
            <App />
        </KioskThemeProvider>
    );
}
